import React from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Users, BookOpen, Briefcase } from 'lucide-react';
import { productsAndServices, growthImages } from '../data/mock';

const ProductsPage = () => {
  const iconMap = {
    operational: Briefcase,
    launch: Rocket,
    workshops: BookOpen,
    ewc: Users,
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--gradient-hero-subtle)', 
        padding: '5rem 1.5rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Our Products & Services
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Structured support for entrepreneurs who want to build with intention
          </p>
          <p className="body-medium" style={{ color: 'var(--text-primary)' }}>
            From launching your business to joining a supportive community
          </p>
        </div>
      </section>

      {/* Products and Services */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container !mx-[10px]">
          <div style={{ display: 'grid', gap: '3rem', maxWidth: '1100px', margin: '0 auto' }}>
            {productsAndServices.map((product) => {
              const Icon = iconMap[product.id];

              return (
                <div 
                  key={product.id} 
                  id={product.id}
                  className={`card ${product.accentColor}`}
                  style={{ 
                    padding: '3rem',
                    position: 'relative',
                    border: product.featured ? '3px solid var(--text-primary)' : 'none',
                    boxShadow: product.featured ? '0 12px 32px rgba(0, 0, 0, 0.12)' : '0 2px 6px rgba(0, 0, 0, 0.03)',
                  }}
                >
                  {product.featured && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'var(--text-primary)',
                      color: 'white',
                      padding: '0.5rem 1.5rem',
                      borderRadius: '2rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontFamily: "'SF Mono', monospace",
                    }}>
                      Featured Service
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '2rem' }}>
                    <div style={{ 
                      width: '4rem', 
                      height: '4rem', 
                      background: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={32} color="var(--text-primary)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 className="heading-1" style={{ marginBottom: '0.5rem' }}>
                        {product.name}
                      </h3>
                      <p className="body-medium" style={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '1rem' }}>
                        {product.tagline}
                      </p>
                      <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                        {product.description}
                      </p>
                    </div>
                  </div>

                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.6)',
                    padding: '2rem',
                    borderRadius: '0.75rem',
                    marginBottom: '2rem'
                  }}>
                    <h4 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                      What's Included:
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {product.includes.map((item, index) => (
                        <li key={index} className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <Link 
                      to="/contact" 
                      className="btn-primary"
                    >
                      {product.ctaText}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container !mx-[10px]">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <img 
                src={growthImages.workspace2} 
                alt="Black women collaborating in modern workspace" 
                style={{ width: '100%', height: 'auto', borderRadius: '0.75rem', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)' }}
              />
            </div>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
                How We Work Together
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {[
                  { 
                    step: '01',
                    title: 'Discovery',
                    description: 'We start with a conversation to understand your vision and needs.'
                  },
                  { 
                    step: '02',
                    title: 'Custom Plan',
                    description: 'We create a tailored approach that fits your stage and goals.'
                  },
                  { 
                    step: '03',
                    title: 'Implementation',
                    description: 'We work alongside you to bring your vision to life with clarity.'
                  },
                  { 
                    step: '04',
                    title: 'Support',
                    description: 'Ongoing guidance and community support as your business grows.'
                  },
                ].map((item) => (
                  <div key={item.step} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div className="font-mono" style={{ 
                      fontSize: '1.25rem', 
                      fontWeight: '600',
                      color: 'var(--text-muted)',
                      minWidth: '3rem'
                    }}>
                      {item.step}
                    </div>
                    <div>
                      <h3 className="heading-3" style={{ marginBottom: '0.25rem' }}>
                        {item.title}
                      </h3>
                      <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container !mx-[10px]" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
            Ready to get started?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-secondary)', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
            Let's talk about which path is right for you and your business.
          </p>
          <Link to="/contact" className="btn-primary">
            Book a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
